import { clsx } from 'clsx'
import { useCallback } from 'preact/hooks'
import { type Signal } from '@preact/signals'
import { type JSX } from 'preact'

import { Typography } from '../typography'

interface Props
	extends Omit<
		JSX.HTMLAttributes<HTMLSelectElement>,
		'onChange' | 'label' | 'size'
	> {
	description: string
	fullWidth?: boolean
	labelClass?: string
	onChange: (id: string, value: string) => void
	label?: string | Signal<string>
	options: {
		label: string | Signal<string>
		value: string | number | Signal<string | number>
	}[]
	selectClass?: string
	id: string
	hasError?: boolean
	error?: string
	helperText?: string
	size?: 'normal' | 'small'
	disabled?: boolean
}

const selectClasses = {
	default:
		'w-full appearance-none rounded-md border border-black-600 bg-black-800 px-4 py-2.5 font-inter text-sm font-normal text-black-100',
	small: 'h-8',
	normal: 'h-10',
	focus: 'focus:border-white focus:ring-white',
	error:
		'focus:border-error focus:ring-error border-error focus-visible:ring-error focus-visible:outline-none',
	disabled: 'disabled:border-input-disabled disabled:text-input-disabled'
}

const Select = ({
	description,
	fullWidth,
	id,
	label = '',
	labelClass,
	options,
	selectClass,
	onChange,
	hasError,
	error,
	helperText,
	size = 'normal',
	disabled,
	...selectProps
}: Props) => {
	const handleInputChange = useCallback(
		(event: JSX.TargetedEvent<HTMLSelectElement, Event>) => {
			onChange(event.currentTarget.id, event.currentTarget.value)
		},
		[onChange]
	)

	return (
		<Typography
			class={clsx(fullWidth && 'w-full', labelClass)}
			color='text-black-100'
			htmlFor={id}
			text={label}
			variant='label3'
		>
			<div
				class={clsx(
					'relative',
					label && 'mt-2.5',
					(error || helperText) && 'mb-2'
				)}
			>
				<select
					class={clsx(
						selectClasses.default,
						hasError && selectClasses.error,
						!hasError && selectClasses.focus,
						disabled && selectClasses.disabled,
						selectClasses[size],
						selectClass
					)}
					disabled={disabled}
					id={id}
					onChange={handleInputChange}
					{...selectProps}
				>
					{description && (
						<option aria-labelledby={id} value='0' disabled selected>
							<Typography text={description} variant='text2' />
						</option>
					)}
					{options?.map(option => (
						<option
							aria-labelledby={id}
							key={option.value}
							value={option.value}
						>
							<Typography text={option.label} variant='text2' />
						</option>
					))}
				</select>
			</div>
			{!hasError && helperText && (
				<Typography
					color='text-black-200'
					text={helperText}
					variant='label3'
					weight='font-normal'
				/>
			)}
			{hasError && (
				<Typography
					color='text-error'
					text={error!}
					variant='label3'
					weight='font-normal'
				/>
			)}
		</Typography>
	)
}

export { Select }
